/**
 * PAS devage administration utilities for AdminView.
 *
 * Algorithm (stateless — recomputed from existing answers on every call):
 *
 *  1. ANCHOR  — find the first Order entry whose itemAge > childPhysicalAge.
 *               If every item is ≤ physical age, the anchor is one past the last item.
 *  2. START   — go back `initialdelta` (default 2) positions from the anchor.
 *  3. BACKWARD PHASE — administer items startIdx → startIdx−1 → … (up to maxno items)
 *               until `thresholdyes` (default 2) Yes (1) answers are collected.
 *               The item where the threshold is met is devFloor.
 *  4. FORWARD PHASE  — administer items startIdx+1 → startIdx+2 → … (up to maxyes items)
 *               until cumulative Not-Yet (3) answers from BOTH phases reach `thresholdno`
 *               (default 2).  The item where the threshold is met is devCeiling.
 *               (This may already be satisfied by answers from the backward phase.)
 *
 * Answer encoding (same as MilestonesView / developmentalAge.ts):
 *   1 = Yes  |  2 = Sometimes  |  3 = Not Yet
 */

import { AdminNodeDto, AdminPageDto } from '../types/adminSegment';
import {
  computeDevelopmentalAge,
  DevelopmentalAgeInput,
  MilestoneAnswerSet,
} from './developmentalAge';
import { AdminDataDto } from '../types/adminState';

// ============================================================
// Tunable parameters
// ============================================================

export interface DevageParams {
  /** Items below the physical-age anchor to begin administration (default 2) */
  initialdelta: number;
  /** Yes (1) answers needed going backward to establish devFloor (default 2) */
  thresholdyes: number;
  /** Cumulative Not-Yet (3) answers (backward + forward) to establish devCeiling (default 2) */
  thresholdno: number;
  /** Maximum items to administer going backward from start (default 15) */
  maxno: number;
  /** Maximum items to administer going forward from start (default 6) */
  maxyes: number;
}

const DEFAULT_PARAMS: DevageParams = {
  initialdelta: 2,
  thresholdyes: 2,
  thresholdno:  2,
  maxno:        15,
  maxyes:       6,
};

// ============================================================
// Public interfaces
// ============================================================

export interface DevageAdminContext {
  node: AdminNodeDto;
  /** Child's chronological age in months */
  childPhysicalAge: number;
  /** MVarName → numeric dataValue already answered: 1=Yes, 2=Sometimes, 3=Not-Yet */
  answers: Map<string, number>;
  /** Partial override of algorithm parameters */
  params?: Partial<DevageParams>;
  languageTypeId?: string;
}

export interface DevageAdminResult {
  /** PageNum to display next, or null when administration is complete */
  nextPageNum: string | null;
  isComplete: boolean;
  /** 1-based Order index where the Yes threshold was met (or the hard backward limit) */
  devFloor: number;
  /** 1-based Order index where the Not-Yet threshold was met (or the hard forward limit) */
  devCeiling: number;
  /** Yes answers accumulated in the backward phase */
  yesCount: number;
  /** Cumulative Not-Yet answers across both phases */
  noCount: number;
  /** Total answered items across the administered (backward + forward) range */
  totalAdministered: number;
}

// ============================================================
// Param / node helpers  (also used by AdminView)
// ============================================================

/** Read a named Param from AdminNodeDto.Params (case-insensitive). */
export function getNodeParam(node: AdminNodeDto, paramName: string): string | null {
  const params = (node.Params ?? (node as any).params ?? []) as Array<{
    Name?: string; name?: string; Val?: string; val?: string;
  }>;
  const match = params.find(
    p => String(p.Name ?? p.name ?? '').toLowerCase() === paramName.toLowerCase()
  );
  return match ? String(match.Val ?? match.val ?? '') : null;
}

/** Return the 'pstype' param value (e.g. 'devage'), or null if absent. */
export function getPasType(node: AdminNodeDto): string | null {
  return getNodeParam(node, 'pstype');
}

// ============================================================
// Answer helpers  (also used by AdminView)
// ============================================================

/**
 * Extract answers for this node's milestones from adminState.data.
 * Only MVarNames that appear in AdminNodeDto.Order are included.
 */
export function extractNodeAnswers(
  node: AdminNodeDto,
  adminData: AdminDataDto[]
): Map<string, number> {
  const answers = new Map<string, number>();
  const orderMvars = new Set(
    (node.Order ?? (node as any).order ?? []).map((o: any) => String(o.Name ?? o.name ?? ''))
  );
  for (const item of adminData ?? []) {
    if (orderMvars.has(item.name)) {
      const num = parseInt(item.value, 10);
      if (!isNaN(num) && num > 0) answers.set(item.name, num);
    }
  }
  return answers;
}

// ============================================================
// Scoring input builder  (also used by AdminView)
// ============================================================

/**
 * Build a DevelopmentalAgeInput for the supplied PAS devage node.
 * Domain key comes from the node's 'domain' param, falling back to node.Name.
 */
export function buildDevelopmentalAgeInput(
  node: AdminNodeDto,
  childPhysicalAge: number,
  answers: Map<string, number>,
  languageTypeId: string = '1'
): DevelopmentalAgeInput {
  const domainKey = getNodeParam(node, 'domain') ?? node.Name ?? 'GENERAL';
  const answerSet: MilestoneAnswerSet = {
    languageTypeId,
    answers: [
      { dataValue: 1, textLabel: 'Yes',       keyValue: 1 },
      { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
      { dataValue: 3, textLabel: 'Not Yet',   keyValue: 3 },
    ],
  };
  return { domainNode: node, domainKey, childPhysicalAge, milestoneAnswers: answers, answerSet };
}

// ============================================================
// Internal types and helpers
// ============================================================

interface OrderItem {
  name:       string;
  itemAge:    number;
  arrayIndex: number; // 0-based
  orderIndex: number; // 1-based (arrayIndex + 1)
}

function parseOrderItems(node: AdminNodeDto): OrderItem[] {
  return (node.Order ?? (node as any).order ?? []).map((o: any, i: number) => ({
    name:       String(o.Name ?? o.name ?? ''),
    itemAge:    parseFloat(String(o.Val ?? o.val ?? '0')) || 0,
    arrayIndex: i,
    orderIndex: i + 1,
  }));
}

function buildMvarToPageNumMap(pages: AdminPageDto[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const page of pages ?? []) {
    const pageNum = String((page as any).PageNum ?? (page as any).pageNum ?? '');
    for (const qList of (page.Questions ?? [])) {
      const mvar = qList.Question?.MvarName;
      if (mvar && pageNum) map.set(mvar, pageNum);
    }
  }
  return map;
}

/**
 * 1-based orderIndex of the item where the Yes threshold was first met going backward,
 * or the deepest backward item if the threshold was never reached.
 */
function resolveDevFloor(
  backwardItems: OrderItem[],
  answers: Map<string, number>,
  thresholdyes: number
): number {
  let yesCount = 0;
  for (const item of backwardItems) {
    if (answers.get(item.name) === 1) {
      yesCount++;
      if (yesCount >= thresholdyes) return item.orderIndex;
    }
  }
  return backwardItems[backwardItems.length - 1].orderIndex;
}

/**
 * 1-based orderIndex of the item where the cumulative Not-Yet threshold was first met
 * (scanning backward items then forward items in administration order),
 * or the highest forward item if the threshold was never reached.
 *
 * Note: if the threshold is met entirely within the backward phase (child is failing
 * items well below expected age), the ceiling is pinned to the start item — the
 * highest-indexed item in the backward range.
 */
function resolveDevCeiling(
  backwardItems: OrderItem[],
  forwardItems:  OrderItem[],
  answers: Map<string, number>,
  thresholdno: number
): number {
  let noCount = 0;

  for (const item of backwardItems) {
    if (answers.get(item.name) === 3) {
      noCount++;
      if (noCount >= thresholdno) {
        // Threshold met while going backward — ceiling = start position
        return backwardItems[0].orderIndex;
      }
    }
  }

  for (const item of forwardItems) {
    if (answers.get(item.name) === 3) {
      noCount++;
      if (noCount >= thresholdno) return item.orderIndex;
    }
  }

  if (forwardItems.length > 0) return forwardItems[forwardItems.length - 1].orderIndex;
  return backwardItems[0].orderIndex;
}

// ============================================================
// Public: next-page selection
// ============================================================

/**
 * Determine which page to administer next within a PAS devage node.
 *
 * Stateless — call after each answer is written to adminState.data.
 * Returns `isComplete = true` once both floor and ceiling thresholds are satisfied
 * (or their hard limits are reached), signalling that scoring can proceed.
 */
export function computeNextDevagePage(ctx: DevageAdminContext): DevageAdminResult {
  const p: DevageParams = { ...DEFAULT_PARAMS, ...(ctx.params ?? {}) };
  const { node, childPhysicalAge, answers } = ctx;

  const orderItems = parseOrderItems(node);
  const n = orderItems.length;

  if (n === 0) {
    return {
      nextPageNum: null, isComplete: true,
      devFloor: 0, devCeiling: 0,
      yesCount: 0, noCount: 0, totalAdministered: 0,
    };
  }

  const pages = (node.Pages ?? (node as any).pages ?? []) as AdminPageDto[];
  const mvarToPageNum = buildMvarToPageNumMap(pages);

  // ── 1. Anchor + start ────────────────────────────────────────────────────
  // anchor = first item whose itemAge > physicalAge; if none, one past the end
  let anchorIdx = orderItems.findIndex(item => item.itemAge > childPhysicalAge);
  if (anchorIdx === -1) anchorIdx = n;

  const startIdx = Math.max(0, anchorIdx - p.initialdelta);

  // ── 2. Backward phase ────────────────────────────────────────────────────
  // Administration order: startIdx, startIdx−1, …, backMin
  const backMin = Math.max(0, startIdx - p.maxno);
  const backwardItems: OrderItem[] = [];
  for (let i = startIdx; i >= backMin; i--) {
    backwardItems.push(orderItems[i]);
  }

  let yesCount = 0;
  for (const item of backwardItems) {
    if (answers.get(item.name) === 1) yesCount++;
  }

  const backwardThresholdMet = yesCount >= p.thresholdyes;
  const backwardAllAnswered  = backwardItems.every(item => answers.has(item.name));
  const backwardDone         = backwardThresholdMet || backwardAllAnswered;

  if (!backwardDone) {
    for (const item of backwardItems) {
      if (!answers.has(item.name)) {
        const pageNum = mvarToPageNum.get(item.name) ?? null;
        if (pageNum) {
          const noCountSoFar = backwardItems.filter(i => answers.get(i.name) === 3).length;
          return {
            nextPageNum: pageNum,
            isComplete: false,
            devFloor:   backwardItems[backwardItems.length - 1].orderIndex,
            devCeiling: backwardItems[0].orderIndex,
            yesCount,
            noCount: noCountSoFar,
            totalAdministered: backwardItems.filter(i => answers.has(i.name)).length,
          };
        }
        console.warn(
          `⚠️ devageAdmin: No page mapped for milestone '${item.name}' in node '${node.Name}' — skipping`
        );
      }
    }
  }

  // ── 3. Forward phase ─────────────────────────────────────────────────────
  // Administration order: startIdx+1, startIdx+2, …, forwardMax
  const forwardMax = Math.min(n - 1, startIdx + p.maxyes);
  const forwardItems: OrderItem[] = [];
  for (let i = startIdx + 1; i <= forwardMax; i++) {
    forwardItems.push(orderItems[i]);
  }

  const noInBack    = backwardItems.filter(i => answers.get(i.name) === 3).length;
  const noInForward = forwardItems.filter(i => answers.get(i.name) === 3).length;
  const totalNo     = noInBack + noInForward;

  const forwardThresholdMet = totalNo >= p.thresholdno;
  const forwardAllAnswered  = forwardItems.length === 0 || forwardItems.every(item => answers.has(item.name));
  const forwardDone         = forwardThresholdMet || forwardAllAnswered;

  if (!forwardDone) {
    for (const item of forwardItems) {
      if (!answers.has(item.name)) {
        const pageNum = mvarToPageNum.get(item.name) ?? null;
        if (pageNum) {
          return {
            nextPageNum: pageNum,
            isComplete: false,
            devFloor:   resolveDevFloor(backwardItems, answers, p.thresholdyes),
            devCeiling: forwardItems[forwardItems.length - 1].orderIndex,
            yesCount,
            noCount: totalNo,
            totalAdministered:
              backwardItems.filter(i => answers.has(i.name)).length +
              forwardItems.filter(i => answers.has(i.name)).length,
          };
        }
        console.warn(
          `⚠️ devageAdmin: No page mapped for milestone '${item.name}' in node '${node.Name}' — skipping`
        );
      }
    }
  }

  // ── 4. Both phases complete ───────────────────────────────────────────────
  return {
    nextPageNum: null,
    isComplete: true,
    devFloor:   resolveDevFloor(backwardItems, answers, p.thresholdyes),
    devCeiling: resolveDevCeiling(backwardItems, forwardItems, answers, p.thresholdno),
    yesCount,
    noCount: totalNo,
    totalAdministered:
      backwardItems.filter(i => answers.has(i.name)).length +
      forwardItems.filter(i => answers.has(i.name)).length,
  };
}
